// Firebase Realtime Database write/subscribe layer. No React here.
// The compat SDK is a global (`firebase`), initialised by firebase-init.js before this module runs.
// Rules of the road:
//  - per-restaurant writes only (`restaurants/<id>` update/set/remove); never write the whole tree
//  - callers do NOT await the server ack before updating the UI; they attach .catch for a toast
//  - `firebaseKey` is in-memory only and is stripped from every write; `undefined` becomes null (dropped)
/* global firebase */

const PATH_RESTAURANTS = 'restaurants';

function db() {
  if (!window.firebaseDB) throw new Error('Firebase is not loaded');
  return window.firebaseDB;
}

// ---------------------------------------------------------------- auth

let authPromise = null;
let authOk = false;
const authListeners = new Set();

function notifyAuth(ok) {
  for (const fn of authListeners) {
    try { fn(ok); } catch { /* listener errors never break auth */ }
  }
}

/**
 * Anonymous sign-in with retry. Resolves true once signed in (cached), false when it failed this time.
 * The original app resolved a one-shot promise at page load; if that failed (offline launch) every later
 * save silently stayed local for the whole session. Here a failure is retried on the next call and on 'online'.
 */
export function ensureAuth() {
  if (authOk) return Promise.resolve(true);
  if (authPromise) return authPromise;
  if (!window.firebaseAuth) { notifyAuth(false); return Promise.resolve(false); }
  const auth = window.firebaseAuth;
  const current = auth.currentUser;
  if (current) {
    authOk = true;
    return Promise.resolve(true);
  }
  authPromise = auth.signInAnonymously()
    .then(() => { authOk = true; notifyAuth(true); return true; })
    .catch(() => { notifyAuth(false); return false; })
    .finally(() => { authPromise = null; });
  return authPromise;
}

export function onAuthChange(fn) {
  authListeners.add(fn);
  return () => authListeners.delete(fn);
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { if (!authOk) ensureAuth(); });
}

// ---------------------------------------------------------------- helpers

/** Deep-copy a value for writing: drop `firebaseKey`, turn undefined into null so the field is removed, keep arrays. */
export function stripForWrite(value) {
  if (Array.isArray(value)) return value.map((v) => (v === undefined ? null : stripForWrite(v)));
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (k === 'firebaseKey') continue;
      out[k] = v === undefined ? null : stripForWrite(v);
    }
    return out;
  }
  return value;
}

/** Snapshot value of `restaurants` (object keyed by id) → array with the in-memory `firebaseKey`. */
export function toArray(data) {
  if (!data || typeof data !== 'object') return [];
  return Object.keys(data).map((key) => ({ ...data[key], firebaseKey: key }));
}

/** Firebase keys cannot contain . $ # [ ] / — restaurant ids must be integers. */
export function isValidKey(id) {
  const s = String(id);
  return s.length > 0 && !/[.$#[\]/]/.test(s);
}

/** Integer ids for a batch created at once (Import). */
export function newIds(count, now = Date.now()) {
  return Array.from({ length: count }, (_, i) => now + i);
}

// ---------------------------------------------------------------- subscriptions

/**
 * Subscribe to a path with a 'value' listener once auth is ready. Returns an unsubscribe function.
 * Safe to call before auth resolves: if unsubscribed meanwhile, the listener is never attached (the original
 * effect could leak listeners here).
 */
export function subscribe(path, onValue, onError) {
  let cancelled = false;
  let ref = null;
  const handler = (snap) => { if (!cancelled) onValue(snap.val()); };
  const errHandler = (err) => { if (!cancelled && onError) onError(err); };
  ensureAuth().then((ok) => {
    if (cancelled) return;
    if (!ok) { if (onError) onError(new Error('auth')); return; }
    ref = db().ref(path);
    ref.on('value', handler, errHandler);
  });
  return () => {
    cancelled = true;
    if (ref) ref.off('value', handler);
  };
}

export function subscribeConnected(onChange) {
  const ref = db().ref('.info/connected');
  const h = (snap) => onChange(!!snap.val());
  ref.on('value', h);
  return () => ref.off('value', h);
}

// ---------------------------------------------------------------- writes (all return the SDK promise)

async function write(fn) {
  const ok = await ensureAuth();
  if (!ok) throw new Error('Not signed in (offline?)');
  return fn(db());
}

export function patchRestaurant(id, patch) {
  if (!isValidKey(id)) return Promise.reject(new Error(`bad restaurant id ${id}`));
  return write((d) => d.ref(`${PATH_RESTAURANTS}/${id}`).update(stripForWrite(patch)));
}

export function putRestaurant(record) {
  if (!isValidKey(record.id)) return Promise.reject(new Error(`bad restaurant id ${record.id}`));
  return write((d) => d.ref(`${PATH_RESTAURANTS}/${record.id}`).set(stripForWrite(record)));
}

/** Create several at once (Import) in one multi-path update. */
export function putRestaurants(records) {
  const updates = {};
  for (const r of records) {
    if (!isValidKey(r.id)) return Promise.reject(new Error(`bad restaurant id ${r.id}`));
    updates[String(r.id)] = stripForWrite(r);
  }
  return write((d) => d.ref(PATH_RESTAURANTS).update(updates));
}

export function deleteRestaurantForever(id) {
  return write((d) => d.ref(`${PATH_RESTAURANTS}/${id}`).remove());
}

export function setPlannedDate(plan) {
  return write((d) => d.ref('plannedDate').set(stripForWrite(plan)));
}

export function clearPlannedDate() {
  return write((d) => d.ref('plannedDate').remove());
}

export function setActiveGame(game) {
  return write((d) => d.ref('activeGame').set(stripForWrite(game)));
}

export function patchActiveGame(patch) {
  return write((d) => d.ref('activeGame').update(stripForWrite(patch)));
}

export function setGuess(profile, amount) {
  return write((d) => d.ref(`activeGame/guesses/${profile}`).set(amount));
}

export function clearActiveGame() {
  return write((d) => d.ref('activeGame').remove());
}

export function setProfilePhotos(photos) {
  return write((d) => d.ref('profilePhotos').set(stripForWrite(photos)));
}
